import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { cridaRecurs, useTancarMenuFora } from "./RecursosComuns";

const PAGE_SIZES = [10, 25, 50];

function comparar(a, b) {
  const aBuit = a === null || a === undefined || a === "";
  const bBuit = b === null || b === undefined || b === "";
  if (aBuit && bBuit) return 0;
  if (aBuit) return 1; // els buits sempre al final
  if (bBuit) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "ca", { sensitivity: "base", numeric: true });
}

/**
 * Llista genèrica dels CRUD de Recursos (mateix patró visual que Productes):
 * cerca, filtres, taula ordenable, targetes en pantalla estreta, menú "…" i paginació.
 *
 * Props específiques de cada recurs:
 * - endpoint / clauDades: endpoint PHP del llistat i clau de la resposta amb les files.
 * - idDe(fila), esActiu(fila), columnes: [{ key, label, num, sortValue(fila), render(fila) }].
 * - textCerca(fila): text on busca el cercador.
 * - filtreFila(fila, filtres): filtres addicionals (tipus, estat…).
 * - renderFiltres({ filtres, setFiltre, dades }): controls addicionals de la barra de filtres.
 * - renderTargeta(fila): { titol, meta, imatge } per a la vista estreta.
 */
function LlistaRecursos({
  endpoint,
  clauDades,
  cosPeticio,
  idDe,
  esActiu,
  columnes,
  textCerca,
  filtreFila,
  renderFiltres,
  renderTargeta,
  placeholderCerca,
  etiquetaPlural,
  femeni = false,
  ordreInicial,
  selected,
  onSelect,
  onObrir,
  onMeta,
  reloadToken,
  setLlista,
  filtres,
  setFiltres,
}) {
  const { rolUsuari } = useUserContext();
  const [dades, setDades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState(ordreInicial || { key: columnes[0].key, direction: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);

  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");

  const setFiltre = (clau, valor) => setFiltres((prev) => ({ ...prev, [clau]: valor }));
  useTancarMenuFora(setMenuObert);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const aplica = () => setIsNarrow(mq.matches);
    aplica();
    mq.addEventListener("change", aplica);
    return () => mq.removeEventListener("change", aplica);
  }, []);

  const cosCrida = JSON.stringify(cosPeticio || {});
  useEffect(() => {
    let cancelat = false;
    const carrega = async () => {
      setLoading(true);
      setError("");
      const resposta = await cridaRecurs(endpoint, JSON.parse(cosCrida));
      if (cancelat) return;
      if (resposta.ok) {
        setDades(resposta.data[clauDades] || []);
        onMeta?.(resposta.data);
      } else {
        setDades([]);
        if (!resposta.silenci) setError(resposta.message || "No s'han pogut carregar les dades.");
      }
      setLoading(false);
    };
    carrega();
    return () => {
      cancelat = true;
    };
    // onMeta és un setter estable del pare; no cal tornar a carregar si canvia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, clauDades, cosCrida, reloadToken]);

  const filtrades = useMemo(() => {
    const text = (filtres.text || "").trim().toLowerCase();
    const llista = dades.filter((fila) => {
      const actiu = esActiu(fila);
      if (filtres.activitat === "actius" && !actiu) return false;
      if (filtres.activitat === "inactius" && actiu) return false;
      if (filtreFila && !filtreFila(fila, filtres)) return false;
      return !text || textCerca(fila).toLowerCase().includes(text);
    });
    const columna = columnes.find((c) => c.key === sortConfig.key);
    const valor = (fila) => (columna?.sortValue ? columna.sortValue(fila) : fila[sortConfig.key]);
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    return [...llista].sort((a, b) => {
      const aBuit = valor(a) === null || valor(a) === undefined || valor(a) === "";
      const bBuit = valor(b) === null || valor(b) === undefined || valor(b) === "";
      if (aBuit || bBuit) return comparar(valor(a), valor(b)); // buits al final en ambdós sentits
      return comparar(valor(a), valor(b)) * dir;
    });
  }, [dades, filtres, sortConfig, columnes, esActiu, filtreFila, textCerca]);

  useEffect(() => {
    setLlista?.(filtrades);
    setPage(1);
  }, [filtrades, setLlista]);

  useEffect(() => {
    if (!selected) return;
    if (!filtrades.some((f) => idDe(f) === idDe(selected))) onSelect?.(null);
  }, [filtrades, selected, idDe, onSelect]);

  const handleSort = (key) =>
    setSortConfig((prev) => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));

  const obrirMenu = (e, fila) => {
    e.stopPropagation();
    const id = idDe(fila);
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 132;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) top = Math.max(marge, rect.top - alt - 4);
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) left = window.innerWidth - ample - marge;
    setMenuPos({ top, left });
    setMenuObert(id);
  };

  const total = filtrades.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginaActual = Math.min(page, totalPages);
  const inici = (paginaActual - 1) * pageSize;
  const paginaItems = filtrades.slice(inici, inici + pageSize);
  const filaMenu = menuObert ? filtrades.find((f) => idDe(f) === menuObert) : null;

  const paginesVisibles = () => {
    const pagines = [];
    const afegir = (n) => {
      if (!pagines.includes(n)) pagines.push(n);
    };
    afegir(1);
    for (let i = paginaActual - 1; i <= paginaActual + 1; i += 1) {
      if (i > 1 && i < totalPages) afegir(i);
    }
    if (totalPages > 1) afegir(totalPages);
    return pagines.sort((a, b) => a - b);
  };

  const th = (col) => (
    <th
      key={col.key}
      className={`${col.num ? "dv2-num" : ""} ${sortConfig.key === col.key ? "is-sorted" : ""}`}
      onClick={() => handleSort(col.key)}
    >
      {col.label} {sortConfig.key === col.key ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  const renderAccions = (fila) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === idDe(fila) ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, fila)}
      >
        …
      </button>
    </div>
  );

  return (
    <div className="dv2-listBody">
      <div className="dv2-filterBar">
        <div className="dv2-filter dv2-filter--grow">
          <span className="dv2-filter-label">Cerca</span>
          <input
            type="text"
            className="dv2-input dv2-input-search"
            placeholder={placeholderCerca}
            value={filtres.text || ""}
            onChange={(e) => setFiltre("text", e.target.value)}
          />
        </div>
        {renderFiltres?.({ filtres, setFiltre, dades })}
        <div className="dv2-filter">
          <span className="dv2-filter-label">{femeni ? "Activitat" : "Estat"}</span>
          <select
            className="dv2-select"
            value={filtres.activitat}
            onChange={(e) => setFiltre("activitat", e.target.value)}
          >
            <option value="actius">{femeni ? "Actives" : "Actius"}</option>
            <option value="inactius">{femeni ? "Inactives" : "Inactius"}</option>
            <option value="tots">{femeni ? "Totes" : "Tots"}</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="dv2-empty">Carregant {etiquetaPlural}…</p>
      ) : error ? (
        <p className="dv2-empty">{error}</p>
      ) : filtrades.length === 0 ? (
        <p className="dv2-empty">No s'ha trobat cap registre.</p>
      ) : (
        <>
          {isNarrow ? (
            <div className="dv2-recordList" style={{ display: "flex" }}>
              {paginaItems.map((fila) => {
                const targeta = renderTargeta(fila);
                return (
                  <article
                    key={idDe(fila)}
                    className={`dv2-record ${idDe(selected) === idDe(fila) ? "is-selected" : ""}`}
                    onClick={() => onSelect?.(fila)}
                    onDoubleClick={() => pucConsultar && onObrir?.(fila, "consultar")}
                  >
                    <div className="dv2-record-head">
                      {targeta.imatge}
                      <div>
                        <p className="dv2-record-title">{targeta.titol}</p>
                        <p className="dv2-record-meta">{targeta.meta}</p>
                      </div>
                    </div>
                    {renderAccions(fila)}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="dv2-tableWrap">
              <table className="dv2-table">
                <thead>
                  <tr>
                    {columnes.map((col) => (col.senseOrdre ? <th key={col.key}>{col.label}</th> : th(col)))}
                    <th className="dv2-table-actions">Accions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginaItems.map((fila) => (
                    <tr
                      key={idDe(fila)}
                      className={`${idDe(selected) === idDe(fila) ? "is-selected" : ""} ${esActiu(fila) ? "" : "is-inactive"}`}
                      onClick={() => onSelect?.(fila)}
                      onDoubleClick={() => pucConsultar && onObrir?.(fila, "consultar")}
                    >
                      {columnes.map((col) => (
                        <td key={col.key} className={col.num ? "dv2-num" : ""}>
                          {col.render(fila)}
                        </td>
                      ))}
                      <td className="dv2-table-actions">{renderAccions(fila)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="dv2-pagination">
            <span>
              Mostrant {inici + 1} a {Math.min(inici + pageSize, total)} de {total} resultats
            </span>
            <div className="dv2-pagination-pages">
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage(1)} aria-label="Primera pàgina">
                «
              </button>
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Pàgina anterior">
                ‹
              </button>
              {paginesVisibles().map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span>…</span>}
                  <button type="button" className={`dv2-pageBtn ${n === paginaActual ? "is-active" : ""}`} onClick={() => setPage(n)}>
                    {n}
                  </button>
                </React.Fragment>
              ))}
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Pàgina següent">
                ›
              </button>
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual >= totalPages} onClick={() => setPage(totalPages)} aria-label="Última pàgina">
                »
              </button>
            </div>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Files per pàgina</span>
              <select
                className="dv2-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}

      {filaMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => { setMenuObert(null); onObrir?.(filaMenu, "consultar"); }}>
                Consultar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => { setMenuObert(null); onObrir?.(filaMenu, "modificar"); }}>
                Modificar
              </button>
            )}
            {pucModificar && (
              <button
                type="button"
                className={`dv2-menu-item ${esActiu(filaMenu) ? "is-danger" : ""}`}
                onClick={() => { setMenuObert(null); onObrir?.(filaMenu, esActiu(filaMenu) ? "desactivar" : "reactivar"); }}
              >
                {esActiu(filaMenu) ? "Desactivar" : "Reactivar"}
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default LlistaRecursos;
